import { ICloneable } from '../../contracts/cloneable.js';
import { IDataComparable } from '../../contracts/data-comparable.js';
import { SearchSnapshotSerializer } from '../../serialization/search-snapshot-serializer.js';
import { MapUtils } from '../../utility/map-utils.js';
import { u8, u16 } from '../core/int.js';
import { AsciiString20 as UniqueId, AsciiString120 as Keyword } from '../core/string.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../dto/data-model.js';
import { cloneData, cloneListOfCloneable, isSameData, isSameList } from '../dto/data-model-utils.js';
import { JSONUtils } from '../../utility/json-utils.js';

export class SearchSnapshot extends DataModel {
  static readonly TYPE_ID: string = 'a5b29992dc444895a8da159f40ac0f7a';

  static readonly VERSION: u8 = u8.make(1);

  private constructor(
    public readonly length: u16,
    public readonly elements: SnapshotElement[],
  ) {
    super();
  }

  get $typeId(): string {
    return SearchSnapshot.TYPE_ID;
  }

  get $version(): u8 {
    return SearchSnapshot.VERSION;
  }

  static create(args: {
    elements: SnapshotElement[],
  }): SearchSnapshot {
    return new SearchSnapshot(
        u16.make(args.elements.length),
        args.elements,
    );
  }

  static fromMap(context: SerializationContext, data: SerializedData): SearchSnapshot {
    const format = context.storage.get<string>('format') ?? 'binary';
    switch(format) {
      case 'json':
        return this.fromJson(context, data);
      case 'binary':
        return this.fromBinary(context, data);
      case 'base64':
        return this.fromBase64(context, data);
    }

    throw new Error(`Unsupported search snapshot serialization format {${format}}.`);
  }

  private static fromJson(context: SerializationContext, data: SerializedData): SearchSnapshot {
    const decodeElement = (elementData: SerializedData): SnapshotElement => {
      const typeId = u8.make(MapUtils.getNumber(elementData, '$typeId'));

      switch(true) {
        case typeId.asNumber() === KeywordList.TYPE_ID.asNumber():
          return KeywordList.fromJson(context, elementData);
        case typeId.asNumber() === EventList.TYPE_ID.asNumber():
          return EventList.fromJson(context, elementData);
      }

      throw new Error(`Unknown SnapshotElement of type {${typeId.asNumber()}}.`);
    };

    return new SearchSnapshot(
        u16.make(MapUtils.getNumber(data, 'length')),
        MapUtils.getList<SnapshotElement>(data, 'elements', (x) => decodeElement(x as SerializedData)),
    );
  }

  private static fromBinary(context: SerializationContext, data: SerializedData): SearchSnapshot {
    return SearchSnapshotSerializer.deserialize(
        context.fromBinaryBlob(
            MapUtils.getRaw(data, 'elements'),
        ),
    );
  }

  private static fromBase64(context: SerializationContext, data: SerializedData): SearchSnapshot {
    return SearchSnapshotSerializer.deserialize(
        context.fromBase64(
            MapUtils.getString(data, 'elements'),
        ),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    const format = context.storage.get<string>('format') ?? 'binary';
    switch(format) {
      case 'json':
        return this.toJson(context);
      case 'binary':
        return this.toBinary(context);
      case 'base64':
        return this.toBase64(context);
    }

    throw new Error(`Unsupported search snapshot serialization format {${format}}.`);
  }

  private toJson(context: SerializationContext): SerializedData {
    return {
      length: this.length.asNumber(),
      elements: this.elements.map((x) => x.toJson(context)),
    };
  }

  private toBinary(context: SerializationContext): SerializedData {
    return {
      elements: context.toBinaryBlob(
          SearchSnapshotSerializer.serialize(this),
      ),
    };
  }

  private toBase64(context: SerializationContext): SerializedData {
    return {
      elements: context.toBase64(
          SearchSnapshotSerializer.serialize(this),
      ),
    };
  }

  clone(): this {
    return new SearchSnapshot(
        this.length,
        this.elements.map((x) => cloneData(x)),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.length.asNumber() === this.length.asNumber() &&
      isSameList(other.elements, this.elements, (a, b) => isSameData(a, b));
  }
}

export abstract class SnapshotElement implements IDataComparable, ICloneable {
  abstract get $typeId(): u8;

  abstract toJson(context: SerializationContext): SerializedData;

  abstract clone(): this;

  abstract isSame(other: this): boolean;
}

export class KeywordList extends SnapshotElement {
  static readonly TYPE_ID: u8 = u8.make(1);

  private constructor(
    public readonly listId: u8,
    public readonly length: u16,
    public readonly keywords: Keyword[],
  ) {
    super();
  }

  get $typeId(): u8 {
    return KeywordList.TYPE_ID;
  }

  static create(args: {
    listId: u8,
    keywords: Keyword[],
  }): KeywordList {
    return new KeywordList(
        args.listId,
        u16.make(args.keywords.length),
        args.keywords,
    );
  }

  static fromJson(context: SerializationContext, data: SerializedData): KeywordList {
    return new KeywordList(
        u8.make(MapUtils.getNumber(data, 'listId')),
        u16.make(MapUtils.getNumber(data, 'length')),
        MapUtils.getList<Keyword>(data, 'keywords', (x) => Keyword.make(x as string)),
    );
  }

  toJson(context: SerializationContext): SerializedData {
    return {
      listId: this.listId.asNumber(),
      length: this.length.asNumber(),
      keywords: this.keywords.map((x) => x.asString()),
    };
  }

  clone(): this {
    return new KeywordList(
        this.listId,
        this.length,
        this.keywords.map((x) => x),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.listId.asNumber() === this.listId.asNumber() &&
      other.length.asNumber() === this.length.asNumber() &&
      isSameList(other.keywords, this.keywords, (a, b) => a.asString() === b.asString());
  }
}

export class KeywordRef implements IDataComparable, ICloneable {
  private constructor(
    public readonly listId: u8,
    public readonly index: u16,
  ) { }

  static create(args: {
    listId: u8,
    index: u16,
  }): KeywordRef {
    return new KeywordRef(
        args.listId,
        args.index,
    );
  }

  static fromJson(context: SerializationContext, data: SerializedData): KeywordRef {
    return new KeywordRef(
        u8.make(MapUtils.getNumber(data, 'listId')),
        u16.make(MapUtils.getNumber(data, 'index')),
    );
  }

  toJson(context: SerializationContext): SerializedData {
    return {
      listId: this.listId.asNumber(),
      index: this.index.asNumber(),
    };
  }

  clone(): this {
    return new KeywordRef(
        this.listId,
        this.index,
    ) as this;
  }

  isSame(other: this): boolean {
    return other.listId.asNumber() === this.listId.asNumber() &&
      other.index.asNumber() === this.index.asNumber();
  }
}

export class Event {
  private constructor(
    public readonly uniqueId: UniqueId,
    public readonly length: u8,
    public readonly keywords: KeywordRef[],
  ) { }

  static create(args: {
    uniqueId: UniqueId,
    keywords: KeywordRef[],
  }): Event {
    return new Event(
        args.uniqueId,
        u8.make(args.keywords.length),
        args.keywords,
    );
  }

  static fromJson(context: SerializationContext, data: SerializedData): Event {
    return this.create({
      uniqueId: UniqueId.make(MapUtils.getString(data, 'uniqueId')),
      keywords: MapUtils.getList<KeywordRef>(data, 'keywords', (x) => KeywordRef.fromJson(context, x as SerializedData)),
    });
  }

  toJson(context: SerializationContext): SerializedData {
    return {
      uniqueId: this.uniqueId.asString(),
      length: this.length.asNumber(),
      keywords: this.keywords.map((x) => x.toJson(context)),
    };
  }

  clone(): this {
    return Event.create({
      uniqueId: this.uniqueId,
      keywords: this.keywords.map((x) => x),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId.asString() === this.uniqueId.asString() &&
      isSameList(other.keywords, this.keywords, (a, b) => isSameData(a, b));
  }
}

export class EventList extends SnapshotElement {
  static readonly TYPE_ID: u8 = u8.make(2);

  private constructor(
    public readonly length: u16,
    public readonly events: Event[],
  ) {
    super();
  }

  get $typeId(): u8 {
    return EventList.TYPE_ID;
  }

  static create(args: {
    events: Event[],
  }): EventList {
    return new EventList(
        u16.make(args.events.length),
        args.events,
    );
  }

  static fromJson(context: SerializationContext, data: SerializedData): EventList {
    return new EventList(
        u16.make(MapUtils.getNumber(data, 'length')),
        MapUtils.getList<Event>(data, 'events', (x) => Event.fromJson(context, x as SerializedData)),
    );
  }

  toJson(context: SerializationContext): SerializedData {
    return {
      length: this.length.asNumber(),
      events: this.events.map((x) => x.toJson(context)),
    };
  }

  clone(): this {
    return new EventList(
        this.length,
        this.events.map((x) => cloneData(x)),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.length.asNumber() === this.length.asNumber() &&
      isSameList(other.events, this.events, (a, b) => isSameData(a, b));
  }
}

export class Artist implements IDataComparable, ICloneable {
  private constructor(
    public readonly uniqueId: UniqueId,
    public readonly length: u8,
    public readonly keywords: KeywordRef[],
  ) { }

  static create(args: {
    uniqueId: UniqueId,
    keywords: KeywordRef[],
  }): Artist {
    return new Artist(
        args.uniqueId,
        u8.make(args.keywords.length),
        args.keywords,
    );
  }

  static fromJson(context: SerializationContext, data: SerializedData): Artist {
    return Artist.create({
      uniqueId: UniqueId.make(MapUtils.getString(data, 'uniqueId')),
      keywords: MapUtils.getList<KeywordRef>(
          data,
          'keywords',
          (x) => KeywordRef.fromJson(context, JSONUtils.decodeRecord(x))),
    });
  }

  toJson(context: SerializationContext): SerializedData {
    return {
      uniqueId: this.uniqueId.asString(),
      length: this.length.asNumber(),
      keywords: this.keywords.map((x) => x.toJson(context)),
    };
  }

  clone(): this {
    return Artist.create({
      uniqueId: this.uniqueId,
      keywords: cloneListOfCloneable(this.keywords),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId.asString() === this.uniqueId.asString() &&
      isSameList(other.keywords, this.keywords, (a, b) => isSameData(a, b));
  }
}

export class Brand implements IDataComparable, ICloneable {
  private constructor(
    public readonly uniqueId: UniqueId,
    public readonly length: u8,
    public readonly keywords: KeywordRef[],
  ) { }

  static create(args: {
    uniqueId: UniqueId,
    keywords: KeywordRef[],
  }): Brand {
    return new Brand(
        args.uniqueId,
        u8.make(args.keywords.length),
        args.keywords,
    );
  }

  static fromJson(context: SerializationContext, data: SerializedData): Brand {
    return Brand.create({
      uniqueId: UniqueId.make(MapUtils.getString(data, 'uniqueId')),
      keywords: MapUtils.getList<KeywordRef>(
          data,
          'keywords',
          (x) => KeywordRef.fromJson(context, JSONUtils.decodeRecord(x))),
    });
  }

  toJson(context: SerializationContext): SerializedData {
    return {
      uniqueId: this.uniqueId.asString(),
      length: this.length.asNumber(),
      keywords: this.keywords.map((x) => x.toJson(context)),
    };
  }

  clone(): this {
    return Brand.create({
      uniqueId: this.uniqueId,
      keywords: cloneListOfCloneable(this.keywords),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId.asString() === this.uniqueId.asString() &&
      isSameList(other.keywords, this.keywords, (a, b) => isSameData(a, b));
  }
}

export class Organizer implements IDataComparable, ICloneable {
  private constructor(
    public readonly uniqueId: UniqueId,
    public readonly length: u8,
    public readonly keywords: KeywordRef[],
  ) { }

  static create(args: {
    uniqueId: UniqueId,
    keywords: KeywordRef[],
  }): Organizer {
    return new Organizer(
        args.uniqueId,
        u8.make(args.keywords.length),
        args.keywords,
    );
  }

  static fromJson(context: SerializationContext, data: SerializedData): Organizer {
    return Organizer.create({
      uniqueId: UniqueId.make(MapUtils.getString(data, 'uniqueId')),
      keywords: MapUtils.getList<KeywordRef>(
          data,
          'keywords',
          (x) => KeywordRef.fromJson(context, JSONUtils.decodeRecord(x))),
    });
  }

  toJson(context: SerializationContext): SerializedData {
    return {
      uniqueId: this.uniqueId.asString(),
      length: this.length.asNumber(),
      keywords: this.keywords.map((x) => x.toJson(context)),
    };
  }

  clone(): this {
    return Organizer.create({
      uniqueId: this.uniqueId,
      keywords: cloneListOfCloneable(this.keywords),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId.asString() === this.uniqueId.asString() &&
      isSameList(other.keywords, this.keywords, (a, b) => isSameData(a, b));
  }
}

export class ArtistList extends SnapshotElement {
  static readonly TYPE_ID: u8 = u8.make(3);

  private constructor(
    public readonly length: u16,
    public readonly artists: Artist[],
  ) {
    super();
  }

  get $typeId(): u8 {
    return ArtistList.TYPE_ID;
  }

  static create(args: {
    artists: Artist[],
  }): ArtistList {
    return new ArtistList(
        u16.make(args.artists.length),
        args.artists,
    );
  }

  static fromJson(context: SerializationContext, data: SerializedData): ArtistList {
    return this.create({
      artists: MapUtils.getList<Artist>(data, 'artists', (x) => Artist.fromJson(context, JSONUtils.decodeRecord(x))),
    });
  }

  toJson(context: SerializationContext): SerializedData {
    return {
      length: this.length.asNumber(),
      artists: this.artists.map((x) => x.toJson(context)),
    };
  }

  clone(): this {
    return ArtistList.create({
      artists: this.artists.map((x) => cloneData(x)),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameList(other.artists, this.artists, (a, b) => isSameData(a, b));
  }
}

export class BrandList extends SnapshotElement {
  static readonly TYPE_ID: u8 = u8.make(4);

  private constructor(
    public readonly length: u16,
    public readonly brands: Brand[],
  ) {
    super();
  }

  get $typeId(): u8 {
    return BrandList.TYPE_ID;
  }

  static create(args: {
    brands: Brand[],
  }): BrandList {
    return new BrandList(
        u16.make(args.brands.length),
        args.brands,
    );
  }

  static fromJson(context: SerializationContext, data: SerializedData): BrandList {
    return this.create({
      brands: MapUtils.getList<Brand>(data, 'brands', (x) => Brand.fromJson(context, JSONUtils.decodeRecord(x))),
    });
  }

  toJson(context: SerializationContext): SerializedData {
    return {
      length: this.length.asNumber(),
      brands: this.brands.map((x) => x.toJson(context)),
    };
  }

  clone(): this {
    return BrandList.create({
      brands: this.brands.map((x) => cloneData(x)),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameList(other.brands, this.brands, (a, b) => isSameData(a, b));
  }
}

export class OrganizerList extends SnapshotElement {
  static readonly TYPE_ID: u8 = u8.make(5);

  private constructor(
    public readonly length: u16,
    public readonly organizers: Organizer[],
  ) {
    super();
  }

  get $typeId(): u8 {
    return OrganizerList.TYPE_ID;
  }

  static create(args: {
    organizers: Organizer[],
  }): OrganizerList {
    return new OrganizerList(
        u16.make(args.organizers.length),
        args.organizers,
    );
  }

  static fromJson(context: SerializationContext, data: SerializedData): OrganizerList {
    return this.create({
      organizers: MapUtils.getList<Organizer>(data, 'organizers', (x) => Organizer.fromJson(context, JSONUtils.decodeRecord(x))),
    });
  }

  toJson(context: SerializationContext): SerializedData {
    return {
      length: this.length.asNumber(),
      organizers: this.organizers.map((x) => x.toJson(context)),
    };
  }

  clone(): this {
    return OrganizerList.create({
      organizers: this.organizers.map((x) => cloneData(x)),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameList(other.organizers, this.organizers, (a, b) => isSameData(a, b));
  }
}

export class SearchSnapshotBuilder extends DataModelBuilder<SearchSnapshot> {
  build(context: SerializationContext, map: SerializedData): SearchSnapshot {
    return SearchSnapshot.fromMap(context, map);
  }
}
